import { WidgetsSdkApis } from "apis/widgetsSdkAPI";
import { useGlobalConfig } from "context/GlobalContext";
import React, { useEffect, useState, useCallback } from "react";

// TODO: Replace with actual pilot values
const CLIENT_ID = 'ameren-dashboard'
const API_URL = 'https://productqaapi-external.bidgely.com'
const ACCESS_TOKEN = '6d02a9bd-6bd9-41d2-a851-54c883b79295' 
const TEST_PAYLOAD = {
  accessToken: ACCESS_TOKEN,
  utilityAccountIdentifier: '7447086986_x1',
  fuelType: 'ELECTRIC',
  accountType: 'RESIDENTIAL',
}

export const WidgetsSdkDemoPage = () => {

  const { rc } = useGlobalConfig()

  const [loading, setLoading] = useState(true)
  const [initData, setInitData] = useState<{messageType : 'ERROR' | 'WIDGET_UI_READY' | 'SUCCESS', data : Record<string, any>}>()
  const [selectedWidget, setSelectedWidget] = useState<string>()

  const encryptPayload = useCallback(async (payload : Record<string, any>) => {
  
    const encKeyPair = await WidgetsSdkApis.getWidgetsSdkEncKeyPair(rc, CLIENT_ID, ACCESS_TOKEN)

    if (encKeyPair.error) {
      throw new Error('Error getting encryption key pair')
    }

    const iv      = encKeyPair.data?.iv!
    const aesKey  = encKeyPair.data?.aesKey!
    
    const ivBytes = new Uint8Array([...iv].map(c => c.charCodeAt(0)));
    const privKey = btoa(aesKey);
    const privUint8 = Uint8Array.from(atob(privKey), c => c.charCodeAt(0));
    const encStr = new TextEncoder().encode(JSON.stringify(payload));
    const SYM_ALGO  = { name: 'AES-CBC', length: 256, iv: ivBytes }
    
    const encryptedKey = await crypto.subtle.importKey('raw', privUint8, SYM_ALGO, false, ['encrypt'])
    const encPayload = await crypto.subtle.encrypt(SYM_ALGO, encryptedKey, encStr)
    const encB64 = btoa(String.fromCharCode(...new Uint8Array(encPayload)));
    return encB64
  }, [rc])

  const initWidgetsSdk = useCallback(async () => {

    setLoading(true)

    if (!window.BidgelyWebSdk) {
      setLoading(false)
      throw new Error('BidgelyWebSdk script not loaded')
    }

    const encryptedPayload = await encryptPayload(TEST_PAYLOAD)

    window.BidgelyWebSdk.initialize('PROD', {
      client_id: CLIENT_ID,
      api_url: API_URL,
      renderWidgetsImmediate: false,
      relayFileDownloads: true,
      payload: encryptedPayload,
    }, (response : any) => {
      setInitData({ messageType: response.messageType, data: response.data })
    })
  
    setLoading(false)
  }, [encryptPayload])

  const checkAndInit = useCallback(() => {
    if (window.BidgelyWebSdk) {
      initWidgetsSdk()
      return true
    }
    return false
  }, [initWidgetsSdk])

  const onChange = (e : React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedWidget(e.target.value)

    setTimeout(() => {

      if (!window.BidgelyWebSdk) {
        console.error('BidgelyWebSdk not available');
        return;
      }

      window.BidgelyWebSdk.renderWidget({
        widgetId: e.target.value,
        instanceId: initData?.data?.instanceId,
      }, (response : any) => {
        console.log('Render Widgets response:', response)
      })
    }, 0)
  }

  useEffect(() => {

    // Check immediately
    if (checkAndInit()) return

    // poll every 100ms for up to 10 seconds
    const interval = setInterval(() => {
      if (checkAndInit()) {
        clearInterval(interval)
      }
    }, 100)

    const timeout = setTimeout(() => {
      clearInterval(interval)
      clearTimeout(timeout)
    }, 10000)

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }

  }, [checkAndInit])

  return (
    <div>
      <h1>Widgets SDK Demo</h1>
      
      {loading && (
        <React.Fragment>  
          <p>Loading...</p> 
        </React.Fragment>
      )}
      
      {!loading && initData && (
        initData.messageType === 'ERROR' ? (
          <p>Error: {JSON.stringify(initData.data)}</p>
        ) : (

          <React.Fragment>
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="widget-selector" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Select Widget:
              </label>
              <select
                id="widget-selector"
                value={selectedWidget || ''}
                onChange={onChange}
                style={{
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  minWidth: '300px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Choose a widget...</option>
                {window.BidgelyWebSdk?.WIDGET_TAG && Object.entries(window.BidgelyWebSdk.WIDGET_TAG).map(([key, value]) => (
                  <option key={key} value={value}>
                    {key.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            {selectedWidget && (
              <div style={{ width: '70%', height: 'auto', margin: '20px auto', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
                <div 
                  id="widget-container"
                  dangerouslySetInnerHTML={{
                    __html: `<${selectedWidget} instanceId="${initData.data.instanceId}"></${selectedWidget}>`
                  }} 
                />
              </div>
            )}
          </React.Fragment>

        )
      )}
    </div>
  )
}